import { isDefined, isEmpty } from '@sgme/fp';
import * as addonActionTypes from 'state/addonForm/addonForm.actionTypes';
import { isQuoteTypeTradable } from 'state/quotes/utils';
import { addKey, addOrPatchKey, removeKey } from 'state/utils/collection';

import type { Action } from '../../actions';
import * as actionTypes from './tradeConfirm.actionTypes';
import type { TradeConfirmationStateMap } from './tradeConfirm.model';
import { defaultBackedNotional, initialTradeConfirmationState } from './tradeConfirm.model';

export function tradeConfirmationsReducer(
  state: TradeConfirmationStateMap = {},
  action: Action,
): TradeConfirmationStateMap {
  switch (action.type) {
    case 'QUOTE_ACKED':
    case addonActionTypes.ADDON_QUOTE_ACKED:
      if (action.type === 'QUOTE_ACKED' && action.workspace !== 'static' && action.workspace !== 'dynamic') {
        return state;
      }
      if (isQuoteTypeTradable(action.quoteType)) {
        return addKey(state, action.quoteId, initialTradeConfirmationState);
      }
      return state;

    case 'QUOTE_REMOVED':
      if (action.workspace !== 'static' && action.workspace !== 'dynamic') {
        return state;
      }
      return removeKey(state, action.quoteId);
    case addonActionTypes.ADDON_QUOTE_REMOVED:
      return removeKey(state, action.quoteId);
    case 'QUOTE_TRADE_REQUESTED':
    case 'STREAMING_TRADE_REQUESTED':
    case addonActionTypes.ADDON_QUOTE_TRADE_REQUESTED:
      return addOrPatchKey(
        state,
        action.quoteId,
        () => ({ hadTraded: true, isTrading: false }),
        initialTradeConfirmationState,
      );
    case actionTypes.UI_CONFIRMED_TERMSHEET_DISCLAIMER:
      return addOrPatchKey(
        state,
        action.quoteId,
        () => ({ hasConfirmedTermsheetDisclaimer: action.checked }),
        initialTradeConfirmationState,
      );
    case actionTypes.UI_CONFIRMED_FRENCH_PUBLIC_OFFERING:
      return addOrPatchKey(
        state,
        action.quoteId,
        () => ({ hasConfirmedFrenchPublicOffering: action.checked }),
        initialTradeConfirmationState,
      );
    case actionTypes.UI_CONFIRMED_KID_DOCUMENTATION:
      return addOrPatchKey(
        state,
        action.quoteId,
        () => ({ hasConfirmedKidDocumentation: action.checked }),
        initialTradeConfirmationState,
      );
    case actionTypes.UI_CONFIRMED_IS_TRADING:
      return addOrPatchKey(state, action.quoteId, () => ({ isTrading: true }), initialTradeConfirmationState);
    case actionTypes.TRADE_CONFIRMATION_RESET:
      return addOrPatchKey(
        state,
        action.quoteId,
        () => ({
          isTrading: false,
          hadTraded: false,
          hasConfirmedTermsheetDisclaimer: false,
          hasConfirmedFrenchPublicOffering: false,
          hasConfirmedKidDocumentation: false,
          backedNotional: defaultBackedNotional,
        }),
        initialTradeConfirmationState,
      );
    case actionTypes.TRADE_CONFIRMATION_INIT_BACK_TO_BACK: {
      const backToBackType = action.backToBackType ?? 'None';
      const isHedgingTrade = backToBackType === 'HedgingTrade';
      const isClientTrade = backToBackType === 'ClientTrade';
      const isScenarioReadOnly = isHedgingTrade || isClientTrade;
      return addOrPatchKey(
        state,
        action.quoteId,
        () => ({
          isBackToBack: { value: action.isBackToBack, isReadonly: true },
          backToBackType: { value: backToBackType, isReadonly: true },
          backedNotional: defaultBackedNotional,
          isDelayedBackToBack: {
            value: action.isDelayedBackToBack ?? undefined,
            isReadonly: isScenarioReadOnly && isDefined(action.isDelayedBackToBack),
          },
          fileCode: { value: action.fileCode, isReadonly: !isEmpty(action.fileCode) },
          isDeltaAdjusted: { value: action.isDeltaAdjusted, isReadonly: isDefined(action.isDeltaAdjusted) },
          noteNominal: {
            value: action.noteNominal,
            isReadonly: isDefined(action.noteNominal) && action.noteNominal > 0,
          },
        }),
        initialTradeConfirmationState,
      );
    }
    case actionTypes.TRADE_CONFIRMATION_SET_BACK_TO_BACK_FIELD_VALUE:
      return addOrPatchKey(
        state,
        action.quoteId,
        s => ({
          [action.field]: { ...s[action.field], value: action.value },
        }),
        initialTradeConfirmationState,
      );
    case actionTypes.TRADE_CONFIRMATION_SET_BACKED_NOTIONAL_UNIT:
      return addOrPatchKey(
        state,
        action.quoteId,
        s => ({
          backedNotional: {
            value: s.backedNotional?.value,
            isReadonly: s.backedNotional?.isReadonly ?? false,
            unit: action.unit,
          },
        }),
        initialTradeConfirmationState,
      );
    case 'QUOTE_TRADE_REQUEST_REJECTED':
    case addonActionTypes.ADDON_QUOTE_TRADE_REQUEST_REJECTED:
      return addOrPatchKey(
        state,
        action.quoteId,
        () => ({
          hadTraded: false,
          isTrading: false,
          hasConfirmedTermsheetDisclaimer: false,
          hasConfirmedFrenchPublicOffering: false,
          hasConfirmedKidDocumentation: false,
        }),
        initialTradeConfirmationState,
      );
    default:
      return state;
  }
}
